import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useIsMobile } from 'hooks/use-media-query';
import { usePaginationParams } from 'hooks/use-pagination-params';
import { useSearchParamState } from 'hooks/use-search-param-state';
import { useTestCategoryLabel } from 'hooks/use-test-category-label';
import { TestListMobile } from 'components/mobile/screens/TestListMobile';
import {
  Plus,
  FileText,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  LayoutTemplate,
  Eye,
  FolderPlus,
  BarChart3,
  Activity,
  Share2,
  AlertCircle,
  Book,
  KeyRound,
  Link as LinkIcon,
  Users,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import { PageHeader } from 'components/ui/page-header';
import { StatusBadge, scoreTextClass, type StatusTone } from 'components/ui/status-badge';
import { StatusTabs, type StatusTabOption } from 'components/ui/status-tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from 'components/ui/table';
import { ColumnsToggle, TableSearch } from 'components/ui/data-table';
import { TablePager } from 'components/ui/pagination';
import { FilterReset } from 'components/ui/filter-button';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { TestFilters, hasTestFilters, type TestFilterValue } from 'components/tests/TestFilters';
import * as TestModule from 'modules/test';
import { isMarketplaceTest } from 'modules/test/marketplace-policy';
import * as AuthModule from 'modules/auth';
import { useOrgStore } from 'store/organization.store';
import { useTranslation } from 'react-i18next';
import { AddToCollectionModal } from 'pages/Collections';
import { useFeature, FEATURES } from 'modules/entitlements';
import { NeedsUpgradeBadge } from 'components/entitlements/NeedsUpgradeBadge';
import { SaveAsTemplateDialog, TemplateSuggestions, type SaveAsTemplateTarget } from 'modules/templates';
import { PERMISSIONS } from 'modules/auth/permissions';
import { cn } from 'lib/utils';

type Lifecycle = 'all' | 'draft' | 'active' | 'finished';
/** Columns the "Ustunlar" menu can hide; the name and the row menu always show. */
type ColumnId = 'category' | 'status' | 'access' | 'submitted' | 'avg' | 'activity';
type Test = TestModule.Types.IEntity.Test;
type Translate = (key: string, opts?: Record<string, unknown>) => string;

// Finished is neither a success nor a failure — neutral tint, ink dot.
const STATUS_TONE: Record<Test['status'], StatusTone> = {
  active: 'success',
  published: 'success',
  draft: 'neutral',
  finished: 'done',
  archived: 'neutral',
};

// Relative "last activity": "2 min ago" · "3 hr ago" · "Yesterday" · weekday · "May 28".
const formatActivity = (date: Date, t: Translate, locale?: string): string => {
  const diff = Date.now() - date.getTime();
  const MIN = 60_000;
  const HR = 60 * MIN;
  const DAY = 24 * HR;
  if (diff < MIN) return t('activity_just_now');
  if (diff < HR) return t('activity_min_ago', { count: Math.floor(diff / MIN) });
  if (diff < DAY) return t('activity_hr_ago', { count: Math.floor(diff / HR) });
  if (diff < 2 * DAY) return t('activity_yesterday');
  if (diff < 7 * DAY) return date.toLocaleDateString(locale || undefined, { weekday: 'short' });
  return date.toLocaleDateString(locale || undefined, { month: 'short', day: 'numeric' });
};

const ACCESS_ICON = 'h-3.5 w-3.5 shrink-0 text-muted-foreground/70';
const ACCESS_TEXT = 'inline-flex min-w-0 items-center gap-1.5 whitespace-nowrap text-[13px] text-foreground/80';

// Which option opens the test. The icons stay gray — the words carry the meaning.
const AccessCell: React.FC<{ test: Test; t: Translate }> = ({ test, t }) => {
  const access = test.access;

  if (access?.mode === 'marketplace' || isMarketplaceTest(test)) {
    return (
      <span className={ACCESS_TEXT}>
        <Book className={ACCESS_ICON} aria-hidden />
        {t('tests_access_marketplace')}
      </span>
    );
  }
  if (access?.mode === 'group') {
    return (
      <span className={cn(ACCESS_TEXT, 'max-w-[170px]')} title={access.groupName ?? undefined}>
        <Users className={ACCESS_ICON} aria-hidden />
        <span className="truncate">{access.groupName || t('group')}</span>
      </span>
    );
  }
  if (access?.mode === 'code') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <KeyRound className={ACCESS_ICON} aria-hidden />
        {access.code ? (
          <code className="inline-flex h-[22px] items-center whitespace-nowrap rounded-md border border-border bg-card px-1.5 font-mono text-[11.5px] font-medium tracking-wide text-foreground/80">
            {access.code}
          </code>
        ) : (
          <span className="text-[13px] text-foreground/80">{t('tests_access_code')}</span>
        )}
      </span>
    );
  }
  if (access?.mode === 'none') {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] text-warning-700 dark:text-warning-400">
        <AlertCircle className="h-3.5 w-3.5 shrink-0 text-warning-500" aria-hidden />
        {t('tests_access_none')}
      </span>
    );
  }
  return (
    <span className={ACCESS_TEXT}>
      <LinkIcon className={ACCESS_ICON} aria-hidden />
      {t('tests_access_public_link')}
    </span>
  );
};

// "118 / 140" with a thin ink bar when the assigned group size is known.
const SubmittedCell: React.FC<{ stats: Test['stats']; locale: string }> = ({ stats, locale }) => {
  const format = (n: number) => n.toLocaleString(locale || undefined);
  if (!stats.submitted && stats.assigned == null) {
    return <span className="text-muted-foreground/60">—</span>;
  }
  if (stats.assigned) {
    const percent = Math.min(100, Math.round((stats.submitted / stats.assigned) * 100));
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] tabular-nums">
          <span className="font-medium text-foreground">{format(stats.submitted)}</span>
          <span className="text-muted-foreground"> / {format(stats.assigned)}</span>
        </span>
        <span aria-hidden className="block h-1 w-[84px] overflow-hidden rounded-full bg-muted">
          <span className="block h-full rounded-full bg-foreground" style={{ width: `${percent}%` }} />
        </span>
      </div>
    );
  }
  return <span className="text-[13px] font-medium tabular-nums text-foreground">{format(stats.submitted)}</span>;
};

// "45 savol · Algebra +1 · Dilnoza Karimova" under the test name.
const testMeta = (test: Test, t: Translate): string => {
  const [firstCollection, ...otherCollections] = test.collections ?? [];
  const collection = firstCollection
    ? `${firstCollection.name}${otherCollections.length ? ` +${otherCollections.length}` : ''}`
    : '';
  return [t('n_questions', { count: test.totalQuestions }), collection, test.author?.name]
    .filter(Boolean)
    .join(' · ');
};

interface TestRowMenuProps {
  test: Test;
  permissions: string[];
  canAddToCollection: boolean;
  onAddToCollection: () => void;
  onDuplicate: () => void;
  onSaveAsTemplate: () => void;
  onDelete: () => void;
}

const TestRowMenu: React.FC<TestRowMenuProps> = ({ test, permissions: p, canAddToCollection, onAddToCollection, onDuplicate, onSaveAsTemplate, onDelete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasRunActions =
    p.includes(PERMISSIONS.MONITORING_MONITOR) ||
    p.includes(PERMISSIONS.TESTS_VIEW_RESULTS) ||
    p.includes(PERMISSIONS.ACCESS_KEYS_CREATE);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={t('more_options')}
        >
          <MoreVertical className="h-4 w-4" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {p.includes(PERMISSIONS.MONITORING_MONITOR) && (
          <DropdownMenuItem className="gap-2" onClick={() => navigate(`/app/tests/${test.id}/monitoring`)}>
            <Activity size={14} />
            {t('tests_action_monitor')}
          </DropdownMenuItem>
        )}
        {p.includes(PERMISSIONS.TESTS_VIEW_RESULTS) && (
          <DropdownMenuItem className="gap-2" onClick={() => navigate(`/app/tests/${test.id}/results`)}>
            <BarChart3 size={14} />
            {t('results')}
          </DropdownMenuItem>
        )}
        {p.includes(PERMISSIONS.ACCESS_KEYS_CREATE) && (
          <DropdownMenuItem className="gap-2" onClick={() => navigate(`/app/tests/${test.id}/access`)}>
            <Share2 size={14} />
            {t('share')}
          </DropdownMenuItem>
        )}
        {hasRunActions && <DropdownMenuSeparator />}
        <DropdownMenuItem className="gap-2" onClick={() => navigate(`/app/tests/preview/${test.id}`)}>
          <Eye size={14} />
          {t('preview')}
        </DropdownMenuItem>
        {canAddToCollection && (
          <DropdownMenuItem className="gap-2" onClick={onAddToCollection}>
            <FolderPlus size={14} />
            {t('add_to_collection')}
          </DropdownMenuItem>
        )}
        {p.includes(PERMISSIONS.TESTS_EDIT) && (
          <DropdownMenuItem className="gap-2" onClick={() => navigate(`/app/tests/${test.id}/settings`)}>
            <Edit size={14} />
            {t('edit')}
          </DropdownMenuItem>
        )}
        {p.includes(PERMISSIONS.TESTS_CREATE) && (
          <DropdownMenuItem className="gap-2" onClick={onDuplicate}>
            <Copy size={14} />
            {t('tests.duplicate')}
          </DropdownMenuItem>
        )}
        {p.includes(PERMISSIONS.TESTS_CREATE) && (
          <DropdownMenuItem className="gap-2" onClick={onSaveAsTemplate}>
            <LayoutTemplate size={14} />
            {t('templates.save_action')}
          </DropdownMenuItem>
        )}
        {p.includes(PERMISSIONS.TESTS_DELETE) && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 text-destructive focus:text-destructive" onClick={onDelete}>
              <Trash2 size={14} />
              {t('delete')}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const LoadingRows: React.FC = () => (
  <div className="divide-y divide-border" aria-hidden>
    {[0, 1, 2, 3, 4].map((row) => (
      <div key={row} className="flex items-center gap-6 px-4 py-3.5">
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className={cn('h-3.5', row % 2 ? 'w-2/5' : 'w-1/2')} />
          <Skeleton className="h-3 w-1/4" />
        </div>
        <Skeleton className="h-5 w-16" />
        <Skeleton className="hidden h-3.5 w-24 lg:block" />
        <Skeleton className="h-3.5 w-12" />
      </div>
    ))}
  </div>
);

const TestListDesktop: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currentOrg } = useOrgStore();
  const {
    state: { organization: authOrg, permissions: p, user },
  } = AuthModule.Context.useAuthContext();
  const { input: searchInput, setInput: setSearchInput, query: searchQuery } = useSearchParamState();
  const { page, pageSize: perPage, setPage, setPageSize: setPerPage } = usePaginationParams({
    defaultPage: 1,
    defaultPageSize: 10,
  });
  const [lifecycle, setLifecycle] = useState<Lifecycle>('all');
  const [filters, setFilters] = useState<TestFilterValue>({});
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [saveTemplateTarget, setSaveTemplateTarget] = useState<SaveAsTemplateTarget | null>(null);
  const [addToCollectionTarget, setAddToCollectionTarget] = useState<{ id: string; label: string } | null>(null);
  const categoryLabel = useTestCategoryLabel();
  const [cols, setCols] = useState<Record<ColumnId, boolean>>({
    category: true,
    status: true,
    access: true,
    submitted: true,
    avg: true,
    activity: true,
  });
  const canUseCollections = useFeature(FEATURES.COLLECTIONS);
  // Collection management is an authoring affordance: candidates (and proctors)
  // can't organize tests into collections. Mirror the permission model rather
  // than role names — candidates hold neither key.
  const canManageCollections = p.includes(PERMISSIONS.COLLECTIONS_CREATE) || p.includes(PERMISSIONS.COLLECTIONS_EDIT);
  // Use current org from store, or fall back to auth profile org (same org used when creating tests)
  const orgId = currentOrg?.id ?? authOrg?.id;

  // Switching org returns to page 1 — but not on first render, so a
  // deep-linked ?page= survives the initial load. (A new search drops `page`
  // itself, in useSearchParamState.)
  const lastOrg = useRef(orgId);
  useEffect(() => {
    if (lastOrg.current && lastOrg.current !== orgId) setPage(1);
    lastOrg.current = orgId;
  }, [orgId, setPage]);

  // Authors/admins see all org tests; team members (candidates) see only tests
  // in collections attached to their teams.
  const isAuthor = Boolean(p.includes(PERMISSIONS.TESTS_CREATE) || p.includes(PERMISSIONS.TESTS_EDIT));
  const listParams = {
    search: searchQuery || undefined,
    orgId: orgId || undefined,
    // Lifecycle tab -> server-side `status` param (supported by GET /api/tests).
    status: lifecycle === 'all' ? undefined : lifecycle,
    createdById: filters.createdById,
    createdFrom: filters.createdFrom,
    createdTo: filters.createdTo,
    collectionId: filters.collectionId,
    page,
    perPage,
  };
  const hasSearchOrFilters = Boolean(searchInput) || hasTestFilters(filters);
  const isFiltering = Boolean(searchQuery) || hasTestFilters(filters) || lifecycle !== 'all';
  const authorList = TestModule.Hooks.useList({ params: listParams, enabled: isAuthor });
  const candidateList = TestModule.Hooks.useAvailableTests({ params: listParams, enabled: !isAuthor });
  const { items: tests, meta, isLoading, isError, isPlaceholderData, refetch } = isAuthor ? authorList : candidateList;
  const totalPages = meta.totalPages || 0;
  // Clamp page when total shrinks (e.g. after deletion of last item on page)
  useEffect(() => {
    if (totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [totalPages, page, setPage]);
  const deleteTest = TestModule.Hooks.useDelete();
  const duplicateTest = TestModule.Hooks.useDuplicate();
  const handleDuplicate = (id: string, title: string) => {
    if (duplicateTest.isPending) return;
    duplicateTest.mutate(
      { id, title: t('tests.copy_title', { title }) },
      { onSuccess: ({ id: copyId }) => navigate(`/app/tests/${copyId}/settings`) },
    );
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters({});
    setPage(1);
  };

  // useDelete already raises the success/error toast — don't double-toast here.
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTest.mutateAsync({ id: deleteTarget });
      refetch();
    } catch {
      /* toast handled by hook */
    } finally {
      setDeleteTarget(null);
    }
  };

  // Per-status counts follow the search and filters; the list API sends them to staff only.
  const counts = meta.statusCounts;
  const allCount = counts ? counts.draft + counts.active + counts.finished + counts.archived : undefined;
  const lifecycleOptions: StatusTabOption<Lifecycle>[] = [
    { value: 'all', label: t('all'), count: allCount },
    { value: 'draft', label: t('draft'), count: counts?.draft },
    { value: 'active', label: t('active'), count: counts?.active },
    { value: 'finished', label: t('finished'), count: counts?.finished },
  ];
  const headerCount = allCount ?? meta.total;

  const renderBody = () => {
    if (isLoading) return <LoadingRows />;
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (tests.length === 0 && !isFiltering) {
      const canCreate = p.includes(PERMISSIONS.TESTS_CREATE);
      return (
        <>
        <EmptyState
          icon={<FileText size={28} />}
          title={t('no_assessments_yet')}
          description={
            p.includes(PERMISSIONS.TESTS_CREATE)
              ? t('create_first_assessment_to_start_evaluating')
              : t('no_assessments_available_yet')
          }
          action={
            p.includes(PERMISSIONS.TESTS_CREATE)
              ? { label: t('create_assessment_2'), onClick: () => navigate('/app/tests/create') }
              : undefined
          }
          secondaryAction={
            canCreate
              ? { label: t('templates.browse'), onClick: () => navigate('/app/tests/templates') }
              : undefined
          }
        />
        {canCreate ? <TemplateSuggestions title={t('templates.recommended_title')} fallbackTitle={t('templates.popular_title')} className="mx-auto w-full max-w-4xl px-4 pb-8" /> : null}
        </>
      );
    }
    if (tests.length === 0) {
      return (
        <EmptyState
          icon={<Search size={28} />}
          title={t('no_results')}
          description={t('try_adjusting_your_search_or_filters')}
          secondaryAction={hasSearchOrFilters ? { label: t('clear_filters'), onClick: resetFilters } : undefined}
        />
      );
    }
    return (
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="min-w-[240px]">{t('name')}</TableHead>
            {cols.category && <TableHead className="hidden w-[152px] md:table-cell">{t('category')}</TableHead>}
            {isAuthor && cols.status && <TableHead className="w-[128px]">{t('status')}</TableHead>}
            {isAuthor && cols.access && <TableHead className="hidden w-[176px] lg:table-cell">{t('tests_col_access')}</TableHead>}
            {isAuthor && cols.submitted && <TableHead className="w-[140px]">{t('tests_col_submitted')}</TableHead>}
            {isAuthor && cols.avg && <TableHead className="w-[92px] text-right">{t('tests_col_avg')}</TableHead>}
            {isAuthor && cols.activity && <TableHead className="hidden w-[128px] lg:table-cell">{t('tests_col_activity')}</TableHead>}
            <TableHead className={isAuthor ? 'w-14' : 'w-28'}>
              <span className="sr-only">{t('more_options')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tests.map((test) => (
            <TableRow key={test.id}>
              {/* max-w-0 lets the title truncate; min-w keeps it from collapsing when the fixed columns take the width. */}
              <TableCell className="min-w-[240px] max-w-0 py-[9px]">
                <div className="flex items-center gap-2">
                  <Link
                    to={isAuthor ? `/app/tests/${test.id}/settings` : `/app/tests/take/${test.id}`}
                    className="truncate text-sm font-medium leading-5 text-foreground hover:underline"
                  >
                    {test.title}
                  </Link>
                  {(test as any).isDemo && (
                    <Badge variant="outline" className="border-border text-[10px] font-normal text-muted-foreground shrink-0">
                      {t('tour.badge_sample', 'Namuna')}
                    </Badge>
                  )}
                </div>
                <p className="mt-0.5 truncate text-[12.5px] leading-[18px] text-muted-foreground">{testMeta(test, t)}</p>
              </TableCell>
              {cols.category && (
                <TableCell className="hidden max-w-0 md:table-cell">
                  {categoryLabel(test.category) ? (
                    <span className="block truncate text-[13px] text-foreground/80" title={categoryLabel(test.category)}>
                      {categoryLabel(test.category)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </TableCell>
              )}
              {isAuthor && cols.status && (
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge tone={STATUS_TONE[test.status] ?? 'neutral'}>{t(test.status)}</StatusBadge>
                    {test.needsUpgrade && (
                      <NeedsUpgradeBadge
                        featureKey={test.needsUpgrade.featureKey}
                        questionCount={test.needsUpgrade.questionCount}
                      />
                    )}
                  </div>
                </TableCell>
              )}
              {isAuthor && cols.access && (
                <TableCell className="hidden lg:table-cell">
                  <AccessCell test={test} t={t} />
                </TableCell>
              )}
              {isAuthor && cols.submitted && (
                <TableCell>
                  <SubmittedCell stats={test.stats} locale={i18n.language} />
                </TableCell>
              )}
              {isAuthor && cols.avg && (
                <TableCell className="text-right text-[13.5px] tabular-nums">
                  {test.stats.avgScore > 0 ? (
                    <span className={cn('font-semibold', scoreTextClass(test.stats.avgScore))}>
                      {Math.round(test.stats.avgScore)}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground/60">—</span>
                  )}
                </TableCell>
              )}
              {isAuthor && cols.activity && (
                <TableCell className="hidden whitespace-nowrap text-[13px] text-muted-foreground lg:table-cell">
                  {formatActivity(test.updatedAt, t, i18n.language)}
                </TableCell>
              )}
              <TableCell className="text-right">
                {isAuthor ? (
                  <TestRowMenu
                    test={test}
                    permissions={p}
                    canAddToCollection={canUseCollections && canManageCollections}
                    onAddToCollection={() => setAddToCollectionTarget({ id: test.id, label: test.title })}
                    onDuplicate={() => handleDuplicate(test.id, test.title)}
                    onSaveAsTemplate={() => setSaveTemplateTarget({ testId: test.id, title: test.title })}
                    onDelete={() => setDeleteTarget(test.id)}
                  />
                ) : (
                  <Button asChild size="sm" className="h-8">
                    <Link to={`/app/tests/take/${test.id}`}>{t('start', 'Start')}</Link>
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div id="tests-page-container" data-tour="tests-page" className="flex flex-col gap-4">
      <PageHeader
        title={
          <span className="flex items-center gap-2.5">
            {t('nav.tests')}
            {headerCount > 0 && (
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
                {headerCount}
              </span>
            )}
          </span>
        }
        actions={
          p.includes(PERMISSIONS.TESTS_CREATE) && (
            <Button asChild size="sm" className="gap-1.5">
              <Link to="/app/tests/create">
                <Plus className="h-4 w-4" />
                {t('create_assessment_2')}
              </Link>
            </Button>
          )
        }
      />

      {isAuthor && (
        <StatusTabs
          aria-label={t('status')}
          options={lifecycleOptions}
          value={lifecycle}
          onChange={(next) => {
            setLifecycle(next);
            setPage(1);
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <TableSearch
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('search_assessments')}
          aria-label={t('search_assessments')}
        />
        {isAuthor && (
          <TestFilters
            orgId={orgId}
            value={filters}
            onChange={(next) => {
              setFilters(next);
              setPage(1);
            }}
            currentUserId={user?.id}
          />
        )}
        {hasSearchOrFilters && <FilterReset onClick={resetFilters} />}
        {isAuthor && (
          <ColumnsToggle
            className="ml-auto"
            columns={[
              { id: 'category', label: t('category'), visible: cols.category },
              { id: 'status', label: t('status'), visible: cols.status },
              { id: 'access', label: t('tests_col_access'), visible: cols.access },
              { id: 'submitted', label: t('tests_col_submitted'), visible: cols.submitted },
              { id: 'avg', label: t('tests_col_avg'), visible: cols.avg },
              { id: 'activity', label: t('tests_col_activity'), visible: cols.activity },
            ]}
            onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
          />
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {/* While the next page loads the previous one stays, dimmed, so nothing jumps. */}
        <div className={cn('transition-opacity', isPlaceholderData && 'opacity-60')} aria-busy={isPlaceholderData || undefined}>
          {renderBody()}
        </div>
        {!isLoading && !isError && meta.total > 0 && (
          <div className="border-t border-border px-4 py-3">
            <TablePager
              page={page}
              pageCount={totalPages || 1}
              pageSize={perPage}
              total={meta.total}
              onPageChange={setPage}
              onPageSizeChange={setPerPage}
              noun={t('pager_noun_tests')}
            />
          </div>
        )}
      </div>

      {saveTemplateTarget ? (
        <SaveAsTemplateDialog target={saveTemplateTarget} onClose={() => setSaveTemplateTarget(null)} />
      ) : null}
      <AddToCollectionModal
        open={Boolean(addToCollectionTarget)}
        onClose={() => setAddToCollectionTarget(null)}
        itemType="test"
        itemId={addToCollectionTarget?.id ?? ''}
        itemLabel={addToCollectionTarget?.label}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('delete_assessment')}
        description={t('trash.test_delete_description')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        loading={deleteTest.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
};

// Phones render the dedicated mobile test list; thin wrapper keeps hook count
// constant across the breakpoint.
const TestList: React.FC = () => (useIsMobile() ? <TestListMobile /> : <TestListDesktop />);

export default TestList;
